"use client";

import { useState, useEffect } from "react";
import { Card, CardContent } from "@/components/ui/card";
import { Bus, MapPin, Loader2 } from "lucide-react";
import axiosInstance from "@/utils/axiosInstance";
import { API_ENDPOINTS } from "@/constants/config";

const LiveTrackingCards = ({ onSelectBus }) => {
  const [loading, setLoading] = useState(true);
  const [cards, setCards] = useState([]);
  const [error, setError] = useState(null);

  useEffect(() => {
    const fetchData = async () => {
      const user = JSON.parse(localStorage.getItem("user") || "{}");
      const schoolId = user?.school;
      if (!schoolId) {
        setError("School not found");
        setLoading(false);
        return;
      }
      try {
        const [vehiclesRes, routesRes] = await Promise.all([
          axiosInstance.get(API_ENDPOINTS.vehicles, {
            headers: { Authorization: `Bearer ${localStorage.getItem("token")}` },
          }),
          axiosInstance.get(API_ENDPOINTS.vehicleRoutesBySchool(schoolId), {
            headers: { Authorization: `Bearer ${localStorage.getItem("token")}` },
          }),
        ]);
        const vehicles = vehiclesRes.data?.data?.vehicles || [];
        const routes = routesRes.data?.data?.vehicleRoutes || [];
        const list = vehicles.map((v) => {
          const route = routes.find((r) => r.vehicle && r.vehicle._id === v._id);
          const shifts = (route?.shifts || []).map((s) => ({
            shiftId: s._id,
            shiftName: `${s.name} (${s.type})`,
          }));
          const currentShiftId = v.currentShiftId ? String(v.currentShiftId) : null;
          const firstShift = shifts[0] || null;
          return {
            vehicleId: v._id,
            vehicleNumber: v.vehicleNumber,
            type: v.type,
            active: !!v.locationSharingActive,
            currentShiftId,
            shifts,
            defaultShiftId: firstShift?.shiftId ?? null,
          };
        });
        setCards(list);
      } catch (err) {
        setError(err.response?.data?.message || "Failed to load buses");
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, []);

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <Loader2 className="h-8 w-8 animate-spin text-[#0263cc]" />
      </div>
    );
  }

  if (error) {
    return (
      <Card className="border-red-200">
        <CardContent className="p-6 text-center text-red-600">{error}</CardContent>
      </Card>
    );
  }

  if (cards.length === 0) {
    return (
      <Card className="border-gray-200">
        <CardContent className="p-8 text-center text-gray-500">
          No vehicles found. Add a vehicle first.
        </CardContent>
      </Card>
    );
  }

  return (
    <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5">
      {cards.map((card) => (
        <Card
          key={card.vehicleId}
          className="cursor-pointer border border-gray-200 hover:border-[#0263cc] hover:shadow-lg transition-all duration-200 overflow-hidden group"
          onClick={() =>
            onSelectBus(
              card.vehicleId,
              card.active && card.currentShiftId ? card.currentShiftId : card.defaultShiftId
            )
          }
        >
          <CardContent className="p-0">
            <div className="p-4 pb-3 flex items-center justify-between gap-3 border-b border-gray-100 bg-gray-50/50">
              <div className="flex items-center gap-3 min-w-0">
                <div className="h-11 w-11 shrink-0 rounded-xl bg-[#0263cc]/10 flex items-center justify-center group-hover:bg-[#0263cc]/15 transition-colors">
                  <Bus className="h-5 w-5 text-[#0263cc]" />
                </div>
                <div className="min-w-0">
                  <p className="font-bold text-gray-900 truncate">
                    {card.vehicleNumber}
                  </p>
                  <p className="text-xs text-gray-500 capitalize">{card.type}</p>
                </div>
              </div>
              <span
                className={`shrink-0 inline-flex items-center gap-1.5 px-2.5 py-1.5 rounded-full text-xs font-semibold ${
                  card.active
                    ? "bg-green-100 text-green-700 border border-green-200"
                    : "bg-red-50 text-red-600 border border-red-100"
                }`}
              >
                <span
                  className={`w-2 h-2 rounded-full ${
                    card.active ? "bg-green-500" : "bg-red-500"
                  }`}
                />
                {card.active ? "Active" : "Inactive"}
              </span>
            </div>
            <div className="p-4 pt-3">
              <p className="text-xs font-medium text-gray-400 uppercase tracking-wide mb-2">
                Shifts
              </p>
              {card.shifts.length > 0 ? (
                <div className="space-y-2">
                  {card.shifts.map((s) => {
                    const isLive =
                      card.active && card.currentShiftId === String(s.shiftId);
                    return (
                      <div
                        key={s.shiftId}
                        className={`flex items-center gap-2.5 py-2 px-2.5 rounded-lg -mx-0.5 transition-colors ${
                          isLive ? "bg-green-50" : "hover:bg-gray-50"
                        }`}
                        onClick={(e) => {
                          e.stopPropagation();
                          onSelectBus(card.vehicleId, s.shiftId);
                        }}
                      >
                        <span
                          className={`shrink-0 w-2.5 h-2.5 rounded-full ${
                            card.active
                              ? isLive
                                ? "bg-green-500 ring-2 ring-green-200"
                                : "bg-gray-300"
                              : "bg-gray-200"
                          }`}
                          title={isLive ? "Live" : ""}
                        />
                        <span
                          className={`text-sm ${
                            isLive ? "font-semibold text-green-800" : "text-gray-700"
                          }`}
                        >
                          {s.shiftName}
                        </span>
                      </div>
                    );
                  })}
                </div>
              ) : (
                <p className="text-sm text-gray-400 flex items-center gap-2">
                  <MapPin className="h-3.5 w-3.5" />
                  No shift
                </p>
              )}
            </div>
          </CardContent>
        </Card>
      ))}
    </div>
  );
};

export default LiveTrackingCards;
